import React, { useMemo } from 'react';
import withLayout from 'hocs/withLayout';
import { PageProps } from 'types/page';
import { SEARCH_ALL } from 'lib/graphql/queries/post';
import { useQuery } from '@apollo/client/react';
import Search from './Search';
import { SearchResults } from 'types/search';

/**
 * Search Layout Wrapper
 *
 * Handles client-side data fetching for the search page.
 * Uses cache-and-network fetchPolicy to show cached results immediately
 * while fetching fresh data in the background.
 *
 * Features:
 * - Cache-and-network fetchPolicy (shows cache immediately, fetches fresh data)
 * - Loading state management
 * - Error handling
 *
 * Used in:
 * - /search page
 *
 * @param props - Layout props
 * @param props.searchParam - Search query string from URL
 * @returns Search layout with data management
 */
const Index = (props: SearchIndexProps) => {
  const { searchParam } = props;

  const variables: SearchQueryVariables = useMemo(
    () => ({
      limit: 9,
      skip: 0,
      search: searchParam || '',
    }),
    [searchParam],
  );

  const { data, loading, networkStatus, error, fetchMore } = useQuery<SearchResults>(
    SEARCH_ALL,
    {
      fetchPolicy: 'cache-and-network', // Show cache immediately, fetch fresh data in background
      variables,
      notifyOnNetworkStatusChange: true,
    },
  );

  const searchResults = useMemo(
    () => data,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data?.publishedPosts, data?.searchUsers],
  );
  const isLoading = (loading && networkStatus !== 3) || networkStatus === 4;
  const loadingMore = networkStatus === 3;

  return (
    <Search
      loading={isLoading}
      loadingMore={loadingMore}
      searchResults={searchResults}
      searchParam={searchParam}
      error={error}
      fetchMore={fetchMore}
      variables={variables}
    />
  );
};

export interface SearchIndexProps extends PageProps {
  searchParam?: string;
}

export type SearchQueryVariables = {
  search?: string;
  limit: number;
  skip: number;
};

export default withLayout(Index, { bottomAd: true, margin: true });
